import { encode } from '@toon-format/toon';
import type { CostVsBudgetChartData, CostVsBudgetSummary } from '../../api/financials-client/financials-client.type';
import {
  COST_VS_OUTPUT_SCHEMA,
  type FlatPeriod,
  type FlatProjectData,
  type FlatSummary,
  type FlattenedCostVsOutputData,
} from './costs-vs-output.type';

/**
 * Flattens a CostVsBudgetSummary into abbreviated format.
 * @param {CostVsBudgetSummary | null} summary - The summary to flatten
 * @returns {FlatSummary | null} The flattened summary or null if input is null
 */
const flattenSummary = (summary: CostVsBudgetSummary | null): FlatSummary | null => {
  if (!summary) {
    return null;
  }

  return {
    c: summary.cost,
    db: summary.daily_burn,
    tc: summary.tasks_completed,
    tco: summary.tasks_committed,
    cpt: summary.cost_per_task,
    pc: summary.points_completed,
    pco: summary.points_committed,
    cpp: summary.cost_per_point,
  };
};

/**
 * Flattens period metadata (month, year, grade, label).
 * @param {CostVsBudgetChartData[]} data - The chart data to extract period information from
 * @returns {FlatPeriod[]} Array of flattened period metadata
 */
const flattenPeriods = (data: CostVsBudgetChartData[]): FlatPeriod[] => {
  return data.map((period) => ({
    m: period.month,
    y: period.year,
    g: period.grade,
    l: period.label || '',
  }));
};

/**
 * Flattens project-level data from all periods.
 * @param {CostVsBudgetChartData[]} data - The chart data containing project information
 * @returns {FlatProjectData[]} Array of flattened project data from all periods
 */
const flattenProjectData = (data: CostVsBudgetChartData[]): FlatProjectData[] => {
  const projectData: FlatProjectData[] = [];

  for (const period of data) {
    if (!period.projects) {
      continue;
    }

    for (const project of period.projects) {
      projectData.push({
        m: period.month,
        y: period.year,
        pid: project.id,
        p: project.name,
        c: project.cost,
        g: project.grade,
        db: project.daily_burn,
        tc: project.completed_tasks,
        tco: project.committed_tasks,
        pc: project.completed_points,
        pco: project.committed_points,
      });
    }
  }

  return projectData;
};

/**
 * Formats the cost-vs-output data for Flora by transforming the nested structure
 * into a flattened format optimized for TOON encoding.
 *
 * The function:
 * 1. Includes summary data (last and average)
 * 2. Flattens nested project arrays into uniform rows
 * 3. Uses abbreviated field names to reduce token count
 * 4. Includes a schema mapping for Flora to interpret the abbreviations
 *
 * @param {CostVsBudgetChartData[] | null} chartData - The chart data array
 * @param {CostVsBudgetSummary | null} lastSummary - The most recent period's summary
 * @param {CostVsBudgetSummary | null} averageSummary - The average across all periods
 * @returns {string} TOON-encoded string of the flattened data, or empty string if no data
 */
const formatFloraCostVsOutputData = (
  chartData: CostVsBudgetChartData[] | null,
  lastSummary: CostVsBudgetSummary | null,
  averageSummary: CostVsBudgetSummary | null,
): string => {
  if (!chartData || chartData.length === 0) {
    return '';
  }

  const flattenedData: FlattenedCostVsOutputData = {
    schema: COST_VS_OUTPUT_SCHEMA,
    last: flattenSummary(lastSummary),
    avg: flattenSummary(averageSummary),
    periods: flattenPeriods(chartData),
    projectData: flattenProjectData(chartData),
  };

  // Don't encode if there's no meaningful data
  if (flattenedData.periods.length === 0) {
    return '';
  }

  return encode(flattenedData);
};

export { formatFloraCostVsOutputData };
